"use client";
import { evaluate, toast } from "@kyan/ui";
import { useDebounceValue } from "usehooks-ts";

import { defaultUnderlying } from "#/config/markets";
import { MIN_RFQ_SIZE } from "#/config/orders";
import { useTradeButtonFeatureFlag } from "#/hooks/featureFlags/useTradeButtonFeatureFlag";
import { usePortfolioValidations } from "#/hooks/usePortfolioValidations";
import { useValidatePerpSize } from "#/hooks/useValidatePerpSize";
import { usePostRFQRequest } from "#/services/kyan/hooks/usePostRFQRequest";
import { RFQOrder, RFQRequestData } from "#/services/kyan/kyan.types";
import { useIsDevMode } from "#/store/app/devMode/devMode";
import { useMarket } from "#/store/app/portfolio/market";
import { usePositionsDrawerMarket } from "#/store/app/positionsDrawer/positionsDrawerMarket";
import { usePositionsDrawerRfqTab } from "#/store/app/positionsDrawer/positionsDrawerRfqTab";
import { usePositionsDrawerSettings } from "#/store/app/positionsDrawer/positionsDrawerSettings";
import { usePositionsDrawerTab } from "#/store/app/positionsDrawer/positionsDrawerTab";
import { useTradePanelTab } from "#/store/app/tradePanel/tradePanelTab";
import { useOption } from "#/store/options/tradePanel/option";
import { useOptionSize } from "#/store/options/tradePanel/optionSize";
import { usePerpIsBuy } from "#/store/perps/tradePanel/perpIsBuy";
import { usePerpSize } from "#/store/perps/tradePanel/perpSize";
import { useRfqRequestDuration } from "#/store/rfq/rfqRequestDuration";
import { useAccount } from "#/store/wallet/account/account";
import { toDirection } from "#/utils/marketActionConverter";
import { getMarketByUnderlying } from "#/utils/marketPair";

const useOptionsDetails = () => {
  const { option } = useOption();
  const { optionSize, setOptionSize } = useOptionSize();
  const { account } = useAccount();

  const underlying = option?.underlying ?? defaultUnderlying;
  const market = getMarketByUnderlying(underlying);

  const [debouncedSize] = useDebounceValue(optionSize, 500);
  const { rfqRequestDuration } = useRfqRequestDuration();

  const hasSizeError = debouncedSize && Number(debouncedSize) < MIN_RFQ_SIZE;
  const isMissingDetails = !debouncedSize || hasSizeError;

  const rfqOrder: RFQOrder = {
    instrument_name: option?.instrument_name ?? "",
    direction: option?.direction || "buy",
    contracts: Number(debouncedSize),
  };

  const rfqRequest: RFQRequestData = {
    taker: account ?? "",
    rfq_orders: [rfqOrder],
    duration: rfqRequestDuration * 60,
  };

  const clearValues = () => {
    setOptionSize("");
  };

  return {
    market,
    isValid:
      Boolean(option && account) &&
      !isMissingDetails &&
      Number(debouncedSize) >= MIN_RFQ_SIZE,
    isMissingDetails,
    rfqRequest,
    clearValues,
    hasSizeError,
  };
};

const usePerpsDetails = () => {
  const { market } = useMarket();
  const { perpSize, setPerpSizeInputValue } = usePerpSize();
  const { perpIsBuy } = usePerpIsBuy();
  const { account } = useAccount();

  const [debouncedSize] = useDebounceValue(perpSize, 500);

  const { hasSizeError } = useValidatePerpSize({ isRfq: true });
  const isMissingDetails = !debouncedSize || hasSizeError;

  const rfqOrder: RFQOrder = {
    instrument_name: `${market.pair}-PERPETUAL`,
    direction: toDirection(perpIsBuy),
    amount: Number(debouncedSize),
  };

  const rfqRequest: RFQRequestData = {
    taker: account ?? "",
    rfq_orders: [rfqOrder],
  };

  const clearValues = () => {
    setPerpSizeInputValue("");
  };

  return {
    market,
    isValid: Boolean(account) && !isMissingDetails && !hasSizeError,
    isMissingDetails,
    rfqRequest,
    clearValues,
    hasSizeError,
  };
};

export const useRFQTradeButtonController = () => {
  const { isPerps } = useTradePanelTab();
  const optionDetails = useOptionsDetails();
  const perpDetails = usePerpsDetails();
  const { setPositionsDrawerTab } = usePositionsDrawerTab();
  const { setPositionsDrawerSettings } = usePositionsDrawerSettings();
  const { setPositionsDrawerRfqTab } = usePositionsDrawerRfqTab();
  const { setMarket: setPositionsDrawerMarket } = usePositionsDrawerMarket();
  const { isDevMode } = useIsDevMode();
  const { isTradeButtonEnabled, tradeButtonTitle } =
    useTradeButtonFeatureFlag();

  const {
    market,
    isValid,
    isMissingDetails,
    rfqRequest,
    clearValues,
    hasSizeError,
  } = isPerps ? perpDetails : optionDetails;

  const { isLiquidated, hasZeroEquity } = usePortfolioValidations(market);

  const label = evaluate({
    [tradeButtonTitle]: !isTradeButtonEnabled,
    "Liquidation Active": isLiquidated,
    "Invalid size": hasSizeError,
    "Enter missing fields": isMissingDetails,
    "Insufficient equity": hasZeroEquity,
    "Request Quote": isValid,
  });
  const buttonLabel = `${label}${isDevMode ? " (Dev)" : ""}`;
  const isDisabled =
    !isTradeButtonEnabled ||
    (!isDevMode && (!isValid || hasZeroEquity || isLiquidated));

  const { mutate, isPending } = usePostRFQRequest({
    onSuccess: (response) => {
      toast.success({
        title: "Post RFQ request successful",
        description: response,
      });

      clearValues();
      setPositionsDrawerTab("rfq");
      setPositionsDrawerMarket(market);
      setPositionsDrawerRfqTab(isPerps ? "perps" : "options");
      setPositionsDrawerSettings({ show: true });
    },
    onError: (error) => {
      toast.error({
        title: "Error with RFQ request",
        description: error.message,
      });
    },
  });

  const handleTrade = () => {
    mutate(rfqRequest);
  };

  return {
    buttonLabel,
    isLoading: isPending,
    isDisabled,
    handleTrade,
  };
};
